import { Accordion, AccordionDetails, AccordionGroup, AccordionSummary, Box, Chip, Divider, List, ListItem } from "@mui/joy";
import { useAuth } from "@providers/AuthProvider";
import { useDocumentTitle, useLocalStorage } from "@uidotdev/usehooks";
import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

const generate = (name, otherOptions) => {
    return {
        name,
        url: name.toLowerCase().replaceAll(' ', '-'),
        ...otherOptions,
    }
};

const settingSections = [
    {
        id: 'data',
        label: 'Data Entry',
        // new: true,
        routes: [
            {...generate('Rapid Reinvestment', {new: true})},
        ]
    },
    {
        id: 'org',
        label: 'Settings & Tools',// 'Organization Settings',
        routes: [
            {...generate('Organization Settings')},
            {...generate('Membership Levels')},
            {...generate('Trainings')},

            {divider: true},

            {
                name: 'Import Data',
                url: 'importers',
            },
            {
                name: 'Widgets',
                url: 'widget-generator',
            },
            // todo: Usage / audit log

            {
                name: 'Add Volunteer Hours',
                url: 'add-hours',
            },

            {divider: true},
            {
                name: 'Billing',
                url: 'https://conductor.maestrocm.com',
                target: '_blank',
                permissions: 'manage billing',
            },
            {
                name: 'User Access',
                url: 'users',
            },
        ],
    },
    {
        id: 'donation',
        label: 'Donations',// Options',
        routes: [
            {...generate('Appeals')},
            {...generate('Campaigns')},
            {...generate('Funds')},
        ],
    },
    {
        id: 'grant',
        label: 'Grants',// Options',
        routes: [
            {...generate('Grants')},
            {
                name: 'Grant Categories',
                url: 'grants/categories',
            },
        ],
    },
    {
        id: 'involvement',
        label: 'Involvements',
        routes: [
            {...generate('Involvements')},
            {
                name: 'Involvement Categories',
                url: 'involvements/categories',
            },
        ],
    },
    {
        id: 'investment',
        label: 'Community Investment',
        routes: [
            {...generate('Public Improvements')},
            {...generate('Public Investments')},
            {...generate('Parks')},
            {...generate('Public Parking')},
        ],
    },
    {
        id: 'property',
        label: 'Properties',// Options',
        routes: [
            {...generate('Registries')},
            {
                name: 'SSAs/BIDs',
                url: 'ssas',
            },
            {...generate('TIFs')},
            {...generate('Zoning')},
        ],
    },
];

const defaultSettingToggle = {
    data: true,
    org: true,
    donation: true,
    grant: true,
    involvement: true,
    investment: true,
    property: true,
};

export default function Settings() {
    useDocumentTitle(`More... | Maestro Community Manager`);

    const { can } = useAuth();
    const location = useLocation();
    const { hash, pathname, search } = location;
    const isActive = (routePath) => pathname.replace('/tools/','').localeCompare(routePath) == 0;

    const [activeSettings, setActiveSettings] = useLocalStorage('active_settings', defaultSettingToggle);

    // check for new settings keys that are missing
    useEffect(() => {
        let hasNewSettings = false;
        let _activeSettings = {...activeSettings};
        for(const defaultSettingsToggleKey in defaultSettingToggle) {
            if(!_activeSettings.hasOwnProperty(defaultSettingsToggleKey)) {
                hasNewSettings = true;
                _activeSettings[defaultSettingsToggleKey] = defaultSettingToggle[defaultSettingsToggleKey];
            }
        }

        if(hasNewSettings) {
            setActiveSettings(_activeSettings);
        }
    }, []);

    return (
        <Box sx={{
            display: 'flex',
            '--mm-settings-sidebar-width': 'max(200px, 15vw)',
        }}>
            <Box sx={{
                position: 'fixed',
                left: {
                    md: '70px',
                },
                top: 'var(--Header-height)',
                bottom: 0,
                width: 'var(--mm-settings-sidebar-width)',
                // flex: '0 0 20vw',
                height: 'calc(100vh - var(--Header-height))',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                borderRight: 1,
                borderColor: 'var(--joy-palette-divider)',
                // borderRightColor: 'palette.neutral.plainHoverBg',
                //theme.palette.neutral.plainHoverBg
            }}>
            <Box sx={{
                height: 'calc(100vh - var(--Header-height))'
            }}>
                <AccordionGroup>
                    {settingSections.map(section => (
                        <Accordion
                            key={section.label}
                            expanded={activeSettings[section.id] == true}
                            onChange={(event, expanded) => {
                                setActiveSettings((_activeSettings) => ({..._activeSettings, [section.id]: expanded}))
                            }}
                        >
                            <AccordionSummary
                            sx={{
                                fontWeight: 'bold',
                                fontSize: 'sm',
                            }}
                            >
                                <div>
                                    {section.label}
                                    {section?.new === true && (
                                        <Chip color="primary" size="sm" sx={{ml: 1}}>NEW!</Chip>
                                    )}
                                </div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List
                                    component={'div'}
                                    sx={theme => ({
                                        p: 1,
                                        gap: 1,
                                        '--ListItem-radius': 'var(--joy-radius-sm)',
                                        '--ListItem-minHeight': '1rem',
                                        '--ListDivider-gap': '0',
                                        '--Icon-fontSize': '1rem',

                                        //var(--variant-plainHoverBg, var(--joy-palette-neutral-plainHoverBg, var(--joy-palette-neutral-100, #F0F4F8)))

                                    })}
                                >
                                {section.routes.map((setting, settingIndex) => {
                                    if(setting.permissions && !can(setting.permissions)) {
                                        return null;
                                    }

                                    if(setting?.divider) {
                                        return <Divider key={`divider-${settingIndex}`}/>
                                    }

                                    return (
                                        <ListItem
                                            key={setting.name}
                                            component={Link}
                                            to={setting.url}
                                            relative="true"
                                            target={setting?.target}
                                            sx={theme => ({
                                                '&:hover': {
                                                bgcolor: theme.palette.neutral.plainHoverBg,
                                                color: theme.palette.neutral.plainHoverColor+' !important',

                                                // var(--variant-plainHoverColor, var(--joy-palette-neutral-plainHoverColor, var(--joy-palette-neutral-900, #0B0D0E)))
                                                },
                                                ...(isActive(setting.url) ? {
                                                    bgcolor: theme.palette.neutral.plainActiveBg+' !important',
                                                    color: theme.palette.neutral.plainHoverColor+' !important',
                                                } : {}),

                                            })}
                                        >
                                            <Box display={'flex'} alignItems={'center'}>
                                                {setting.name}
                                                {setting?.new === true && (
                                                    <Chip color="primary" variant={isActive(setting.url) ? 'solid' : 'soft'} size="sm" sx={{ml: 1}}>NEW!</Chip>
                                                )}
                                            </Box>
                                        </ListItem>
                                    )
                                })}

                                </List>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </AccordionGroup>
            </Box>
            </Box>
            <Box sx={{
                marginLeft: 'var(--mm-settings-sidebar-width)',
                // maxWidth is to force the importer column chooser table to have a scrollbar
                maxWidth: {
                    xs: 'calc(100vw - var(--mm-settings-sidebar-width))',
                    md: 'calc(100vw - var(--mm-settings-sidebar-width) - 70px - 1rem)',
                },
                minHeight: 'calc(100vh - var(--Header-height) - 100px)',
                flex: '1'
            }}>
                <Outlet/>
            </Box>
        </Box>

    )
}
